import React, { Fragment } from 'react';
import { graphql, withPrefix } from 'gatsby';
import PropTypes from 'prop-types';
import {
  PageHeader,
  SimpleDataTableWithButtons,
  DangerousWrapper,
  Section,
  toLineItem,
  toSectionsURL,
  getFootnotes
} from '@budget-monorepo/shared-ui';
import Layout from '../components/Layout';
import HelmetData from '../components/HelmetData';

const LineItemPage = (props) => {
  const { location, data: { content, site } } = props;
  // eslint-disable-next-line camelcase
  const { modifiedTime, mg_mod_date, mg_mod_datetime } = content.parent;
  const layoutProps = {
    activeIndex: 1,
    above: () => <PageHeader title={content.title} category={content.budgetSection} />,
    pageTitle: content.title,
    dataTimeStamp: modifiedTime,
    buildTimeStamp: site.buildTime
  };
  const vetoTableProps = {
    filterable: true,
    headers: content.summaryTable.headers,
    data: []
  };
  const lineItemsMax = content.lineItems.length;
  let i = 0;
  const allData = [];
  const filteredData = [];
  for (; i < lineItemsMax; i += 1) {
    const item = content.lineItems[i];
    // Filter out any line items without spending category table data.
    if (item && item.budgetTracking && item.historicalBudget) {
      const {
        govLevelOne, govLevelTwo, govLevelThree, lineItemNumber
      } = item;
      const slug = toSectionsURL({
        href: '/enacted/',
        sections: govLevelOne === govLevelTwo ? [govLevelOne, govLevelThree, lineItemNumber] : [govLevelOne, govLevelTwo, govLevelThree, lineItemNumber]
      });
      const [tracking] = item.budgetTracking.data;
      const [spending] = item.historicalBudget.data;
      const { projected } = spending;
      const {
        house1, conference, gaa, veto, override
      } = tracking;
      const itemRow = {
        // eslint-disable-next-line jsx-a11y/anchor-is-valid
        lineItemNumber: (
          <span>
            <a href={withPrefix(slug)}>{toLineItem(item.lineItemNumber)}</a>
            {` ${item.lineItemName}`}
          </span>),
        lineItemName: (
          <span>
            <a href={withPrefix(slug)}>{toLineItem(item.lineItemNumber)}</a>
            {` ${item.lineItemName}`}
          </span>),
        projected,
        house1,
        conference,
        gaa,
        veto,
        override,
        vetoLanguage: (item.veto && item.veto.language) || ''
      };
      allData.push(itemRow);
      if (item.veto && item.veto.language) {
        filteredData.push(itemRow);
      }
    }
  }
  const [currentData, setCurrentData] = React.useState(allData);
  vetoTableProps.data = currentData;
  const getSubComponent = (row, column, activeButton) => (
    (activeButton === 'vetoLanguage') ? (
      <Fragment>
        <div className="rt-tr" role="row" key={`${row.original.lineItemNumber}}`}>
          <div className="rt-td rt-expandable" style={{ flex: '35 0 auto', width: '35px', maxWidth: '35px' }} />
          <div className="rt-td left string" style={{ flex: '1 1 auto', width: '100%', maxWidth: '100%' }}>
            <DangerousWrapper rawHtml={`<b>Veto Explanation</b>: ${row.original.vetoLanguage}`} htmlTag="span" className="veto-exp" />
          </div>
        </div>
      </Fragment>
    ) : null
  );


  const filterVetoes = ({ accessor }) => {
    const onlyVetoes = (accessor === 'vetoLanguage');
    if (onlyVetoes) {
      setCurrentData(filteredData);
    } else {
      setCurrentData(allData);
    }
  };

  const simpleDataTableWithButtonsProps = {
    defaultExpLevel: 0,
    onClickCallback: (value) => filterVetoes(value),
    simpleDataTableProps: {
      table: vetoTableProps,
      maxColWidth: 300,
      download: location.pathname,
      title: `${content.title} ${content.budgetSection}`,
      pagination: true,
      pageSize: 50,
      getSubComponent,
      renderSubComponent: (activeButton) => (activeButton === 'vetoLanguage')
    },
    treeTableButtonsProps: {
      a11yTitle: content.title,
      headerLevel: 2,
      buttons: [{
        info: 'View all of the line items.',
        text: 'View all line items',
        accessor: 'lineItemNumber',
        expLevel: 0
      }, {
        info: 'View all of the line items that have been vetoed by the governor.',
        text: 'View only vetoed line items',
        accessor: 'vetoLanguage',
        expLevel: 1
      }]
    }
  };

  const metaTitle = content.title;
  const tableHeaders = content.summaryTable.headers.map((header) => header.name).join(', ');
  const metaDescription = `View all line items data by ${tableHeaders}. Filter and sort line items by line item number and name.`;
  return(
    <Fragment>
      <HelmetData {...{
        metaTitle,
        metaDescription,
        mg_mod_date,
        mg_mod_datetime
      }}
      />
      <Layout {...layoutProps}>
        <Section>
          <SimpleDataTableWithButtons {...simpleDataTableWithButtonsProps} />
        </Section>
        {content.footnotes && (
          <Section>
            {getFootnotes(content.footnotes)}
          </Section>
        )}
      </Layout>
    </Fragment>
  );
};

LineItemPage.propTypes = {
  data: PropTypes.object,
  location: PropTypes.object
};

export default LineItemPage;

export const query = graphql`
  query LineItemPageQuery {
    site {
      ...SiteMetadata
    }
    content: pagesJson(fields: { slug: { eq: "/line-item/" } }) {
      title
      budgetSection
      summaryTable {
        headers {
          name
          type
          align
          value
          footnote
        }
      }
      lineItems {
        lineItemNumber
        lineItemName
        govLevelOne
        govLevelTwo
        govLevelThree
        veto  {
          language
        }
        historicalBudget {
          data {
            projected
          }
        }
        budgetTracking {
          data {
            house1
            conference
            gaa
            veto
            override
          }
        }
      }
      footnotes {
        index
        text
      }
      # Get the last modified timestamp for the file powering the page
      ...PagesJsonDataTimeStamp
    }
  }
`;
